import debug from 'debug'
import events from 'Store/events'
import API from '@/api'
import {
  SettingListByFeature,
  UsersListByFeature,
  UsersListByExten
} from './helper'

const dbg = debug('store:settings:actions:')

export default {
  load: function ({ state, rootState, commit }) {
    events.mask()
    API.getSettingsCanWatchUsers()
      .then(UsersListByFeature)
      .then(({ list, errors }) => {
        errors.forEach(events.pubError)
        commit('SET_SUB_ACCOUNTS', UsersListByExten(list))
        this.dispatch('settings/getBlacklist')
      })
      .catch(err => {
        dbg(err)
        events.pubError(
          API.getException(err) || 'Failed to load settings users'
        )
      })
      .finally(() => {
        events.unmask()
      })
  },

  init: function ({ commit, dispatch }, data) {
    commit('INIT', data)
  },

  initToggle: function ({ dispatch }) {
    return API.getToggleDestinations().then(({ data }) => {
      dispatch('toggle/init', data)
    })
  },

  settingsCurrentUser: {
    root: true,
    handler ({ commit, dispatch }, data) {
      commit('SET_CURRENT_USER', data)
      commit('SET_OWNER_ID', data.id)
      dispatch('setSIPUARealmID', data.realm_id, { root: true })
      return dispatch('loadAccSettings', data.id)
    }
  },

  loadAccSettings: function ({ commit, state, rootGetters }, id) {
    const isLocal = id === state.user.id
    const permID = state.subAccounts
      .find(acc => acc.user_id === id)?.subscriber_id
    const canRead = (perm) => isLocal
      ? rootGetters[`permissions/${perm}`]
      : rootGetters[`permissions/${perm}For`](permID)
    const apiCalls = [
      [canRead('dndRead'), 'do_not_disturb'],
      [canRead('cwtRead'), 'call_waiting'],
      [canRead('icomRead'), 'intercom'],
      [canRead('fwdRead'), 'forward'],
      [canRead('fmeRead'), 'follow_me'],
      [canRead('ctdRead'), 'click_to_dial'],
      [canRead('didsRead'), 'dids'],
      [canRead('screenPopRead'), 'screen_pop']
    ]
    const mapAPICalls = () =>
      apiCalls.map(([canRead, key]) =>
        canRead && API.getUserSettings(id, key))
    return Promise.allSettled(
      [
        ...mapAPICalls(),
        canRead('ecidProfRead') && API.getECIDProfiles(),
        canRead('ecidLocRead') && API.getECIDLocations(),
        canRead('otpRead') && API.getOTPConfig(),
        rootGetters['permissions/userPreferencesRead'] && API.getUserPreferences(id),
        API.getBlacklist(),
        API.getUserPreferences(id)
      ]
    ).then(data => {
      const { list, errors } = SettingListByFeature(data)
      errors.forEach(events.pubError)
      commit('SET_CALLWAIT', list.call_waiting?.enabled === '1')
      commit('SET_DND', list.do_not_disturb?.enabled === '1')
      commit('SET_DIDS', list.dids)
      commit('INIT_INTERCOM', list.intercom)
      commit('SET_CALLFWD', {
        enabled: !!list.forward?.to,
        number: list.forward?.to
      })
      commit('SET_FOLLOWME_ALL', list.follow_me)
      commit('SET_CTD_ALL', list.click_to_dial)
      commit('SET_SCREEN_POP', list.screen_pop)
      commit('SET_OTP_ALL', list.otp_configuration)
      commit('SET_EMERGENCY', {
        profiles: list.emergency_caller_id_callback_profiles,
        locations: list.emergency_caller_id_dispatchable_locations
      })
      commit('SET_SHOW_MISSED_CALLS', list.preferences?.show_missed_calls === '1')
      commit('SET_BLACKLIST', list.blacklists.data)
      commit('SET_CLEARLY_ANYWHERE_LANDING_PAGE', list.preferences?.clearly_anywhere_landing_page)
      return API.getFmeTimeConditions()
    }).then(({ data }) => {
      if (Array.isArray(data)) {
        commit('SET_FME_TIME_COND_LIST', data.filter(tc => tc.enabled))
      }
    })
  },

  reloadOTPSettings ({ commit }) {
    return Promise.allSettled(
      [
        API.getOTPConfig()
      ]
    ).then(response => {
      const { list, errors } = SettingListByFeature(response)
      errors.forEach(events.pubError)
      commit('SET_OTP_ALL', list.otp_configuration)
    })
  },

  reset: function ({ commit }) {
    commit('RESET')
    commit('toggle/RESET')
  },

  setOwnerID: function ({ commit }, id) {
    commit('SET_OWNER_ID', id)
  },

  setDND: function ({ commit, state, getters }, enabled) {
    return API.settingsDND(state.ownerID, enabled).then(() => {
      commit('SET_DND', enabled)
      commit('sipua/SET_DND',
        { enabled, code: state.featureCodes.dnd, exten: getters.currentUserExten },
        { root: true })
      const key = 'settings.notify.dnd' + (enabled ? 'On' : 'Off')
      events.pubNotify({ text: key, status: 'success' })
    }).catch(err => {
      dbg(err)
      dbg(err.response?.data)
      events.pubError(API.getException(err) || 'API failed to update dnd.')
    })
  },

  setCallwaiting: function ({ state, commit }, enabled) {
    return API.settingsCallWaiting(state.ownerID, enabled).then(() => {
      const key = 'settings.notify.cw' + (enabled ? 'On' : 'Off')
      events.pubNotify({ text: key, status: 'success' })
      commit('SET_CALLWAIT', enabled)
    }).catch(err => {
      dbg(err)
      dbg(err.response?.data)
      events.pubError(API.getException(err) || 'API failed to update call waiting.')
    })
  },

  setClearlyAnywherelandingPage: function ({ commit, state }, currentPath) {
    const userID = state.user.id
    API.setClearlyAnywherelandingPage(userID, currentPath).then((data) => {
      commit('SET_CLEARLY_ANYWHERE_LANDING_PAGE', currentPath)
    }).catch(err => {
      dbg(err)
      dbg(err.response?.data)
      events.pubError(API.getException(err) || 'API failed to set default Clearly Anywhere landing page')
    })
  },

  setIntercom: function ({ state, commit }, { enabled, icom }) {
    const flags = { ...state.intercom }
    flags[icom] = enabled
    return API.settingsIntercom(state.ownerID, flags).then(() => {
      const key = 'settings.notify.icom' + (enabled ? 'On' : 'Off')
      events.pubNotify({ text: key, status: 'success', args: { icom } })
      commit('SET_INTERCOM', { enabled, icom })
    }).catch(err => {
      dbg(err)
      dbg(err.response?.data)
      events.pubError(API.getException(err) || 'API failed to update intercom settings.')
    })
  },

  getNotifications: function ({ state, commit }) {
    return API.getNotificationSettings().then(notifications => {
      commit('SET_NOTIFICATIONS', notifications.data)
    }).catch(err => {
      dbg(err)
      dbg(err.response?.data)
      events.pubError(API.getException(err) || 'API failed to get notification settings.')
    })
  },

  setNotifications: function ({ state, commit }, notifications) {
    return API.setNotificationSettings(notifications).then(() => {
      commit('SET_NOTIFICATIONS', notifications)
    }).catch(err => {
      dbg(err)
      dbg(err.response?.data)
      events.pubError(API.getException(err) || 'API failed to set notification settings.')
    })
  },

  setCallFwd: function ({ commit }, data) {
    commit('SET_CALLFWD', data)
  },

  setFollowmeEnabled: function ({ commit }, val) {
    commit('SET_FOLLOWME_ENABLED', val)
  },

  setFollowmeConfirm: function ({ commit }, val) {
    commit('SET_FOLLOWME_CONFIRM', val)
  },

  setFollowmeMarkAnswered: function ({ commit }, val) {
    commit('SET_FOLLOWME_ANSWERED_ELSEWHERE', val)
  },

  setFollowmePrefix: function ({ commit }, val) {
    commit('SET_FOLLOWME_PREFIX', val)
  },

  setFollowmeClientCID: function ({ commit }, val) {
    commit('SET_FOLLOWME_CLIENTCID', val)
  },

  setExternCIDName: function ({ commit }, val) {
    commit('SET_EXTERN_CID_NAME', val)
  },

  setExternCIDNum: function ({ commit }, val) {
    commit('SET_EXTERN_CID_NUM', val)
  },

  setFollowmeToutFirst: function ({ commit }, val) {
    commit('SET_FOLLOWME_TOUT_FIRST', val)
  },

  setFollowmeToutAll: function ({ commit }, val) {
    commit('SET_FOLLOWME_TOUT_ALL', val)
  },

  setFeatureCodes: function ({ commit }) {
    return API.getFeatureCodes().then(resp => {
      if (resp.data?.success !== true) {
        dbg('WARN: failed to get feature codes')
        return
      }
      commit('SET_FEATURE_CODES', resp.data.data)
    }).catch(err => {
      dbg(err)
      events.pubError('WARNING: failed to get feature codes. Will use defaults')
    })
  },

  setFollowmeNumbers: function ({ commit }, numbers) {
    commit('SET_FOLLOWME_NUMBERS', numbers)
  },

  setFollowmeStrategy: function ({ commit }, strategy) {
    commit('SET_FOLLOWME_STRATEGY', strategy)
  },

  setEmergencyProfileCID: function ({ commit }, id) {
    commit('SET_ECID_PROFILE_ID', id)
  },

  setEmergencyLocationCID: function ({ commit }, id) {
    commit('SET_ECID_LOCATION_ID', id)
  },

  setUsername: function ({ commit }, name) {
    commit('SET_USERNAME', name)
  },

  setUserEmail: function ({ commit }, email) {
    commit('SET_USEREMAIL', email)
  },

  setOTPMethod: function ({ commit }, method) {
    commit('SET_OTP_OPT', { opt: 'method', val: method })
  },

  setOTPEmail: function ({ commit }, email) {
    commit('SET_OTP_OPT', { opt: 'email', val: email })
  },

  setOTPPhoneNum: function ({ commit }, num) {
    commit('SET_OTP_OPT', { opt: 'phoneNum', val: num })
  },

  setOTPProvURI: function ({ commit }, uri) {
    commit('SET_OTP_OPT', { opt: 'provURI', val: uri })
  },

  setOTPEnabled: function ({ commit }, enabled) {
    commit('SET_OTPENABLED', enabled)
  },

  setFmeTimeCondID: function ({ commit }, id) {
    commit('SET_FME_TIME_COND_ID', id)
  },

  setClickToDial: function ({ state, commit }, data) {
    return API.settingsClickToDial(state.ownerID, { destination: data.destination })
      .then(() => {
        const key = 'settings.notify.ctdSaved'
        events.pubNotify({ text: key, status: 'success' })
        commit('SET_CTD_ALL', data)
      }).catch(err => {
        dbg(err)
        events.pubError(API.getException(err) || 'API failed to update click-to-dial destination.')
      })
  },

  setShowMissedCalls: function ({ commit, state }, enabled) {
    return API.settingsShowMissedCalls(state.ownerID, enabled).then(() => {
      const key = 'settings.notify.smc' + (enabled ? 'On' : 'Off')
      events.pubNotify({ text: key, status: 'success' })
      commit('SET_SHOW_MISSED_CALLS', enabled)
    }).catch(err => {
      dbg(err)
      dbg(err.response?.data)
      events.pubError(API.getException(err) || 'API failed to update show missed calls setting.')
    })
  },

  setScreenPopType: function ({ commit }, type) {
    commit('SET_SCREEN_POP_TYPE', type)
  },

  setScreenPopTrigger: function ({ commit }, trigger) {
    commit('SET_SCREEN_POP_TRIGGER', trigger)
  },

  setScreenPopUrl: function ({ commit }, url) {
    commit('SET_SCREEN_POP_URL', url)
  },

  setScreenPop: function ({ state, commit }, data) {
    return API.settingsScreenPop(state.ownerID, data)
      .then(() => {
        const key = 'settings.notify.screenPopSaved'
        events.pubNotify({ text: key, status: 'success' })
        commit('SET_SCREEN_POP', data)
      }).catch(err => {
        dbg(err)
        events.pubError(API.getException(err) || 'API failed to update screen pop settings.')
      })
  },

  getBlacklist: function ({ commit }) {
    return API.getBlacklist().then(resp => {
      commit('SET_BLACKLIST', resp.data.data)
    }).catch(err => {
      dbg(err)
      events.pubError('Failed to get blacklist')
    })
  },

  addBlacklist: function ({ commit }, number) {
    commit('ADD_BLACKLIST', number)
  },

  setBlacklist: function ({ commit }, list) {
    commit('SET_BLACKLIST', list)
  },

  removeBlacklist: function ({ commit }, number) {
    if (!number.id) {
      commit('REMOVE_BLACKLIST', number)
      return
    }
    events.mask()
    return API.deleteBlacklist(number.id).then(() => {
      commit('REMOVE_BLACKLIST', number)
      this.dispatch('settings/getBlacklist')
    }).catch(err => {
      dbg(err)
      events.pubError('Failed to get blacklist')
    }).finally(() => {
      events.unmask()
    })
  },

  saveBlacklist: function ({ commit }, number) {
    events.mask()
    return API.saveBlacklist(number).then((resp) => {
      const newItem = { id: resp.data.id, name: resp.data.name, number: resp.data.number }
      commit('ADD_BLACKLIST', newItem)
      events.pubNotify({ text: 'settings.blacklist.addSuccess', status: 'success' })
    }).catch(err => {
      dbg(err)
      events.pubError({ key: 'settings.blacklist.addFailed' })
    }).finally(() => {
      events.unmask()
    })
  },

  saveAllBlacklist: function ({ commit }, list) {
    list = list.filter(item => item.number && item.name)
    events.mask()
    return API.saveAllBlacklist(list).then((resp) => {
      commit('SET_BLACKLIST', resp.data)
    }).catch(err => {
      dbg(err)
      events.pubError('Failed to get blacklist')
    }).finally(() => {
      events.unmask()
    })
  }
}
